/**
 * Analytics API Methods (V2)
 */

import { apiClient } from '../client/apiClient';

export type AdTagsRequest = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
  lowPrice?: number;
  highPrice?: number;
};

/** GAM targeting keys the backend derives from the searched bounding box. */
export type AdTagsAttributes = {
  dma?: string;
  cnty?: string;
  state?: string;
  prange?: string;
};

type AdTagsResponse = {
  data?: Array<Record<string, string>>;
};

/**
 * Fetch the GAM targeting keys for a bounding box. Returns `{}` when the box is
 * larger than the backend's limit (10,000 sq mi) or matches no area — the
 * envelope carries an error instead of data in that case, which is not fatal:
 * the ad request just goes out without the area-derived keys.
 *
 * Request failures (PerimeterX 403 blocks, timeouts) resolve `{}` for the same
 * reason instead of throwing — a missing targeting key must never surface as a
 * reported exception. Cancellations still reject so callers can discard them.
 */
export async function fetchAdTags(request: AdTagsRequest, signal?: AbortSignal): Promise<AdTagsAttributes> {
  const { minLat, maxLat, minLon, maxLon, lowPrice, highPrice } = request;
  let response: AdTagsResponse;
  try {
    response = await apiClient<AdTagsResponse>({
      path: '/v2/analytics/adtags',
      queryParams: {
        minLat,
        maxLat,
        minLon,
        maxLon,
        ...(lowPrice == null ? {} : { lowPrice }),
        ...(highPrice == null ? {} : { highPrice }),
      },
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (signal?.aborted) throw error;
    return {};
  }

  // The backend serializes the map as a list of single-key objects:
  // `[{ cnty: 'san_francisco' }, { dma: 'san francisco ca' }]`.
  return (response.data ?? []).reduce<AdTagsAttributes>((attributes, entry) => Object.assign(attributes, entry), {});
}
